import useModal from '__components/Modals/useModal';
import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  allowedPathsWhileFailedBilling,
  allowedPatternWhileFailedBilling,
  blockedRedirectPath,
  useBillingState,
} from 'utility/billing';
import { useBillingPermissions } from 'ts-components/hooks/permissions/billing';
import { useBillingWarningDisplay } from './useBillingWarningDisplay';
import { WarningModal } from './modals/Warning';
import { AdminFailureModal, UserFailureModal } from './modals/Failure';

type BillingRoutingCoreProps = {
  canShowWarnFailing: boolean;
  canShowWarnShutdown: boolean;
  markWarnFailingShown: () => void;
  markWarnShutdownShown: () => void;
};

const BillingRoutingCore = ({
  canShowWarnFailing,
  canShowWarnShutdown,
  markWarnFailingShown,
  markWarnShutdownShown,
}: BillingRoutingCoreProps) => {
  const location = useLocation();
  const history = useHistory();

  const { isBlocked, isWarnFailing, isWarnShutdown, lockDate } =
    useBillingState();

  const { canEditPaymentMethods } = useBillingPermissions();

  const { pathname } = location;

  const [warnFailingModalProps, , warnFailingModal] = useModal({
    handleSubmit: async () => {
      history.push('/settings/billing/details');
    },
  });

  const [adminFailureModalProps, , adminFailureModal] = useModal({
    handleSubmit: async () => {
      history.push('/settings/billing/details');
    },
  });

  const [userFailureModalProps, , userFailureModal] = useModal({
    handleSubmit: async () => {},
  });

  const isPathAllowed = useMemo(() => {
    if (allowedPatternWhileFailedBilling.test(pathname)) {
      return true;
    }

    const isAlwaysAllowedPath = allowedPathsWhileFailedBilling.some((allowed) =>
      pathname.startsWith(allowed)
    );

    if (isAlwaysAllowedPath) {
      return true;
    }

    return false;
  }, [pathname]);

  const showWarnFailing = useCallback(() => {
    markWarnFailingShown();
    warnFailingModal.show();
  }, [warnFailingModal, markWarnFailingShown]);

  const showAdminFailure = useCallback(() => {
    markWarnShutdownShown();
    adminFailureModal.show();
  }, [adminFailureModal, markWarnShutdownShown]);

  const showUserFailure = useCallback(() => {
    markWarnShutdownShown();
    userFailureModal.show();
  }, [userFailureModal, markWarnShutdownShown]);

  // Handle rerouting when the business is blocked
  useEffect(() => {
    if (isPathAllowed) {
      return;
    }

    if (isBlocked) {
      history.replace(blockedRedirectPath);
    }
  }, [pathname, history, isBlocked, isPathAllowed]);

  // Handle warn failing dialog - for users who have access to edit payment methods
  useEffect(() => {
    if (isWarnFailing && canEditPaymentMethods && canShowWarnFailing) {
      showWarnFailing();
    }
  }, [
    isWarnFailing,
    showWarnFailing,
    canEditPaymentMethods,
    canShowWarnFailing,
    warnFailingModal,
  ]);

  // Handle warn shutdown dialog - for all users
  useEffect(() => {
    if (isWarnShutdown && canShowWarnShutdown) {
      if (canEditPaymentMethods) {
        showAdminFailure();
      } else {
        showUserFailure();
      }
    }
  }, [
    isWarnShutdown,
    canEditPaymentMethods,
    canShowWarnShutdown,
    showAdminFailure,
    showUserFailure,
  ]);

  return (
    <>
      <WarningModal {...warnFailingModalProps} />
      <AdminFailureModal {...adminFailureModalProps} lockDate={lockDate} />
      <UserFailureModal {...userFailureModalProps} />
    </>
  );
};

type BillingRoutingProps = {
  enabled: boolean;
};

export const BillingRouting = (props: BillingRoutingProps) => {
  const { enabled } = props;

  const {
    isLoading,
    canShowWarnFailing,
    canShowWarnShutdown,
    markWarnFailingShown,
    markWarnShutdownShown,
  } = useBillingWarningDisplay(enabled);

  if (!enabled || isLoading) {
    return null;
  }

  return (
    <BillingRoutingCore
      canShowWarnFailing={canShowWarnFailing}
      canShowWarnShutdown={canShowWarnShutdown}
      markWarnFailingShown={markWarnFailingShown}
      markWarnShutdownShown={markWarnShutdownShown}
    />
  );
};
